import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Grid, Card, CardContent, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, useTheme, useMediaQuery } from '@mui/material';
import { Add } from '@mui/icons-material';
import { notificationsAPI } from '../services/api';

const safeArray = (val) => Array.isArray(val) ? val : [];

const Notifications = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: '',
    userId: '',
  });
  const [error, setError] = useState('');
  const queryClient = useQueryClient();
  const { data: notifications = [], isLoading } = useQuery('notifications', async () => (await notificationsAPI.getAll()).data);

  const createMutation = useMutation(notificationsAPI.create, {
    onSuccess: () => {
      queryClient.invalidateQueries('notifications');
      handleClose();
    },
    onError: (error) => {
      setError(error.response?.data?.message || t('notifications.errors.createFailed'));
    },
  });

  const handleOpen = () => {
    setFormData({ title: '', message: '', type: '', userId: '' });
    setError('');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({ title: '', message: '', type: '', userId: '' });
    setError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  if (notifications && notifications.message) {
    return <Typography color="error">{notifications.message}</Typography>;
  }
  const notificationsSafe = safeArray(notifications);

  const getTypeColor = (type) => {
    switch (type) {
      case 'PAYMENT_DUE': return 'error';
      case 'COMPLAINT_UPDATE': return 'warning';
      case 'MAINTENANCE_SCHEDULE': return 'info';
      case 'GENERAL': return 'default';
      default: return 'default';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'PAYMENT_DUE': return t('notifications.types.paymentDue');
      case 'COMPLAINT_UPDATE': return t('notifications.types.complaintUpdate');
      case 'MAINTENANCE_SCHEDULE': return t('notifications.types.maintenanceSchedule');
      case 'GENERAL': return t('notifications.types.general');
      default: return type;
    }
  };

  return (
    <Box sx={{ p: isMobile ? 1 : 2 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'flex-start' : 'center', 
        mb: 2,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 2 : 0
      }}>
        {/*<Typography variant="h4">Notifications Management</Typography> */}
        <Button 
          variant="contained" 
          startIcon={<Add />} 
          onClick={handleOpen}
          fullWidth={isMobile}
          sx={{ minHeight: isMobile ? 44 : 'auto' }}
        >
          {t('notifications.addNotification')}
        </Button>
      </Box>
      {isLoading ? (
        <Typography>{t('common.loading')}</Typography>
      ) : notificationsSafe.length === 0 ? (
        <Typography>{t('notifications.noNotificationsFound')}</Typography>
      ) : (
        <Grid container spacing={isMobile ? 2 : 3}>
          {notificationsSafe.map((notification) => (
            <Grid item xs={12} sm={6} md={4} key={notification.notificationId}>
              <Card sx={{ 
                height: '100%',
                boxShadow: isMobile ? 1 : 2
              }}>
                <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                  <Typography variant={isMobile ? "body1" : "h6"} sx={{ fontWeight: 'bold', mb: 1 }}>
                    {notification.title}
                  </Typography>
                  <Typography 
                    color="textSecondary" 
                    sx={{ 
                      mb: 1,
                      fontSize: isMobile ? '12px' : '14px'
                    }}
                  >
                    {t('notifications.user')}: {notification.userId}
                  </Typography>
                  <Typography 
                    variant={isMobile ? "caption" : "body2"} 
                    sx={{ 
                      mb: 2,
                      display: '-webkit-box',
                      WebkitLineClamp: isMobile ? 2 : 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}
                  >
                    {notification.message}
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 1, 
                    alignItems: 'center',
                    flexWrap: 'wrap'
                  }}>
                    <Chip 
                      label={getTypeLabel(notification.type)} 
                      color={getTypeColor(notification.type)}
                      size="small"
                    />
                    <Chip 
                      label={notification.isRead ? t('notifications.read') : t('notifications.unread')} 
                      color={notification.isRead ? 'default' : 'primary'}
                      size="small"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ 
          position: isMobile ? 'sticky' : 'static', 
          top: 0, 
          bgcolor: 'background.paper', 
          zIndex: 1 
        }}>
          {t('notifications.addNewNotification')}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Box sx={{ display: 'grid', gap: 2 }}>
              <TextField 
                label={t('notifications.form.title')} 
                value={formData.title} 
                onChange={e => setFormData({ ...formData, title: e.target.value })} 
                required 
                fullWidth 
                size={isMobile ? "small" : "medium"}
              />
              <TextField 
                label={t('notifications.form.message')} 
                value={formData.message} 
                onChange={e => setFormData({ ...formData, message: e.target.value })} 
                required 
                fullWidth 
                multiline 
                rows={isMobile ? 2 : 3}
                size={isMobile ? "small" : "medium"}
              />
              <TextField 
                label={t('notifications.form.type')} 
                value={formData.type} 
                onChange={e => setFormData({ ...formData, type: e.target.value })} 
                required 
                fullWidth 
                size={isMobile ? "small" : "medium"}
              />
              <TextField 
                label={t('notifications.form.userId')} 
                value={formData.userId} 
                onChange={e => setFormData({ ...formData, userId: e.target.value })} 
                required 
                fullWidth 
                size={isMobile ? "small" : "medium"}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ 
            position: isMobile ? 'sticky' : 'static', 
            bottom: 0, 
            bgcolor: 'background.paper', 
            p: 2,
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 1 : 0
          }}>
            <Button 
              onClick={handleClose}
              fullWidth={isMobile}
              sx={{ minHeight: isMobile ? 44 : 'auto' }}
            >
              {t('common.cancel')}
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={createMutation.isLoading}
              fullWidth={isMobile}
              sx={{ minHeight: isMobile ? 44 : 'auto' }}
            >
              {createMutation.isLoading ? t('notifications.form.adding') : t('common.add')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Notifications; 